<template>
  <div class="flex flex-col gap-2 text-left" data-testid="coverage-status-filter">
    <span class="text-sm font-semibold text-textPrimary">{{ label }}</span>
    <div class="flex flex-wrap gap-2">
      <button
        v-for="status in COVERAGE_STATUS_ORDER"
        :key="status"
        type="button"
        :aria-pressed="isSelected(status)"
        class="inline-flex items-center gap-2 rounded-full border border-solid px-3 py-1 text-sm font-semibold transition-colors"
        :class="isSelected(status) ? SELECTED_CLASSES[status] : 'border-lightGray30 bg-white text-textPrimary'"
        :data-testid="`coverage-status-chip-${status.toLowerCase()}`"
        @click="toggle(status)"
      >
        {{ COVERAGE_STATUS_SHORT_LABELS[status] }}
        <span
          v-if="counts"
          class="rounded-full bg-white/70 px-1.5 text-xs"
          :data-testid="`coverage-status-count-${status.toLowerCase()}`"
        >{{ formatCount(counts[status]) }}</span>
      </button>
    </div>
    <p
      v-if="counts && countsHint"
      class="text-xs text-[#68688D]"
      data-testid="coverage-status-counts-hint"
    >
      {{ countsHint }}
    </p>
    <p v-if="coveredHint" class="text-xs text-[#68688D]" data-testid="coverage-status-covered-hint">
      {{ coveredHint }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { CoverageStatus } from '@/Network/Types/Responses/CoverageResponses'
import type { CoverageCounts } from '@/Network/Types/Responses/CoverageResponses'
import { COVERAGE_STATUS_SHORT_LABELS } from '@/utils/coverage'
import { COVERAGE_STATUS_ORDER } from '@/utils/coverageFilters'

const props = withDefaults(
  defineProps<{
    modelValue: CoverageStatus[]
    counts?: CoverageCounts | null
    label?: string
    /** Escopo da contagem (ex.: só as escalas carregadas); ausente = sem nota. */
    countsHint?: string | null
  }>(),
  { counts: null, label: 'Vaga do plantão', countsHint: null }
)
const emit = defineEmits<{ 'update:modelValue': [value: CoverageStatus[]] }>()

const SELECTED_CLASSES: Record<CoverageStatus, string> = {
  [CoverageStatus.OPEN]: 'border-feedBackAlert bg-[#FEF3E7] text-[#8A4B00]',
  [CoverageStatus.DOCTOR]: 'border-primary bg-primary5 text-primary40',
  [CoverageStatus.UNIDENTIFIED]: 'chip-unidentified border-[#68688D] text-[#3E3E5A]'
}

const isSelected = (status: CoverageStatus) => props.modelValue.includes(status)
const formatCount = (value: number | undefined) => (value ?? 0).toLocaleString('pt-BR')

function toggle(status: CoverageStatus) {
  const next = isSelected(status)
    ? props.modelValue.filter((item) => item !== status)
    : [...props.modelValue, status]
  emit('update:modelValue', COVERAGE_STATUS_ORDER.filter((item) => next.includes(item)))
}

const coveredHint = computed(() => {
  const covered =
    props.modelValue.length === 2 &&
    isSelected(CoverageStatus.DOCTOR) &&
    isSelected(CoverageStatus.UNIDENTIFIED)
  if (!covered) return null
  const total = props.counts ? props.counts.DOCTOR + props.counts.UNIDENTIFIED : null
  return `Selecionados: Com médico + Ocupado s/ ident.${total !== null ? ` (= plantões cobertos, ${formatCount(total)})` : ''}`
})
</script>

<style scoped>
.chip-unidentified {
  background: repeating-linear-gradient(135deg, #eeeef3 0 5px, #e1e1ea 5px 10px);
}
</style>
